import { useCallback } from 'react';
import { useGetAddressById } from '@/api/useGetAddressById';
import { useSaveAddress } from '@/api/useSaveAddress';

// Extract a 2-letter country ISO code from address.countryIso (if provided)
function getAddressCountryIso(addr?: any): string | null {
  if (!addr) return null;
  const v = (addr as any)?.countryIso;
  if (typeof v === 'string') {
    const s = v.trim().toUpperCase();
    if (!s) return null;
    if (s === 'NOR') return 'NO'; // normalize 3-letter to 2-letter
    if (s.length === 2) return s;
  }
  return null;
}

/**
 * Hook for resolving the current Address using both sources:
 * - Query by id (useGetAddressById)
 * - Last saved address from mutation (useSaveAddress)
 * The most recent/explicit update (save mutation) takes priority.
 * Also exposes a function to determine whether VAT applies for the current address.
 */
export function useAddresses(addressId?: string | number | null) {
  const { data: fetchedById } = useGetAddressById(addressId);
  const saveMutation = useSaveAddress();

  // Picks the most relevant address in this order: saved mutation -> fetched by id -> provided fallback -> null
  const getAddress = useCallback(
    (fallback?: any) => {
      const saved = saveMutation?.data as any | undefined;
      if (saved && typeof saved === 'object') return saved;
      if (fetchedById && typeof fetchedById === 'object') return fetchedById as any;
      if (fallback && typeof fallback === 'object') return fallback;
      return null;
    },
    [saveMutation?.data, fetchedById]
  );

  // Returns true if VAT should apply for the current (or provided) address.
  // Business rule: apply VAT for Norway (NO). If unknown, default to true (safer until explicit non-NO).
  const appliesVat = useCallback(
    (addr?: any) => {
      const a = addr ?? getAddress();
      const iso = getAddressCountryIso(a);
      if (!iso) return true; // default include VAT until a non-NO country is set
      return iso === 'NO';
    },
    [getAddress]
  );

  return { getAddress, appliesVat } as const;
}
